import Animator from '../utils/Animator';
import Envelope from "../utils/Envelope";

export default class PluginZoomPan {

    extent = {
        left: 0,
        top: 0,
        width: 0,
        height: 0,
    }

    resolution = 1

    constructor(designer, {
        animationSpeed = 500

    } = {}) {

        this.designer = designer;
        this.animationSpeed = animationSpeed;
    }

    install() {

        const me = this;
        this.animator = new Animator(this.extent)
            .easing(Animator.Easing.Cubic.Out)
            .onUpdate(e => {
                me.designer.resize();
                me.designer.resolution = me.extent.width / me.designer.width;
                me.designer.extent = { ...me.extent };
            });
        this.animatorResolution = new Animator(this)
            .easing(Animator.Easing.Cubic.Out)
            .onUpdate(e => {
                me.designer.resize();
                let dx = me.backupZoomCenterScreen.x * me.resolution;
                let dy = me.backupZoomCenterScreen.y * me.resolution;

                let extent = {};
                extent.left = me.backupZoomCenter.x - dx;
                extent.top = me.backupZoomCenter.y - dy;
                extent.width = me.resolution * me.designer.width;
                extent.height = me.resolution * me.designer.height;

                me.designer.resolution = me.resolution;
                me.designer.extent = extent;
            });

        this.updateAnimator = () => {

            if (this.shutdown || (!this.animator.isBusy && !this.animatorResolution.isBusy))
                return;

            if (this.animator)
                this.animator.update();
            if (this.animatorResolution)
                this.animatorResolution.update();

            window.requestAnimationFrame(this.updateAnimator);
        };

        if (this.designer.mode === "view")
            return;

        const dom = this.designer.dom;
        dom.addEventListener("mousedown", this.onMouseDownHandler = this.onMouseDown.bind(this));
        dom.addEventListener("mouseup", this.onMouseUpHandler = this.onMouseUp.bind(this));
        dom.addEventListener("mousemove", this.onMouseMoveHandler = this.onMouseMove.bind(this));
        dom.addEventListener("mousewheel", this.onMouseWheelHandler = this.onMouseWheel.bind(this));
        dom.addEventListener("contextmenu", this.onContextmenuHandler = this.onContextmenu.bind(this));
    }

    uninstall() {

        this.shutdown = true;

        if (this.designer.mode === "view")
            return;

        const dom = this.designer.dom;
        dom.removeEventListener("mousedown", this.onMouseDownHandler);
        dom.removeEventListener("mouseup", this.onMouseUpHandler);
        dom.removeEventListener("mousemove", this.onMouseMoveHandler);
        dom.removeEventListener("mousewheel", this.onMouseWheelHandler);
        dom.removeEventListener("contextmenu", this.onContextmenuHandler);
    }

    setToModel(model) {

        this.setToRect({
            left: model.x,
            top: model.y,
            width: model.width,
            height: model.height
        })
    }

    setToRect(rect, buffer = true) {

        const env = new Envelope(rect);

        if (buffer)
            env.bufferByPercent(.2);

        let { width: rectWidth, height: rectHeight, left: rectLeft, top: rectTop } = env;
        let { width, height } = this.designer;

        let resH = rectWidth / width;
        let resV = rectHeight / height;

        let res = Math.max(resH, resV);

        let acWidth = width * res;
        let acHeight = height * res;

        let rectCenterX = rectLeft + rectWidth / 2;
        let rectCenterY = rectTop + rectHeight / 2;

        let acLeft = rectCenterX - acWidth / 2;
        let acTop = rectCenterY - acHeight / 2;

        this.designer.resolution = res;
        this.designer.extent = { left: acLeft, top: acTop, width: acWidth, height: acHeight };
    }

    flyToModel(model) {

        let { x, y } = model.getViewLocation();
        let { width, height } = model.getViewSize();

        this.flyToRect({
            left: x,
            top: y,
            width: width,
            height: height
        })
    }

    flyToModels(models) {

        if (models.length === 0)
            return;
        if (models.length === 1) {
            this.flyToModel(models[0]);
            return;
        }

        let { x, y } = models[0].getViewLocation();
        let { width, height } = models[0].getViewSize();

        let env = new Envelope({
            left: x,
            top: y,
            width: width,
            height: height
        });

        models.forEach(c => {

            let { x, y } = c.getViewLocation();
            let { width, height } = c.getViewSize();

            env.unionByXY(x, y);
            env.unionByXY(x + width, y + height);
        });

        this.flyToRect(env);
    }

    flyToResolution(res) {

        let cx = this.designer.extent.left + this.designer.extent.width / 2;
        let cy = this.designer.extent.top + this.designer.extent.height / 2;

        let w = this.designer.width * res;
        let h = this.designer.height * res;

        let x = cx - w / 2;
        let y = cy - h / 2;

        this.designer.flyToRect({
            left: x,
            top: y,
            width: w,
            height: h,
        }, false);
    }

    flyToRect(rect, buffer = true) {

        const env = new Envelope(rect);

        if (buffer)
            env.bufferByPercent(.2);

        let { width: rectWidth, height: rectHeight, left: rectLeft, top: rectTop } = env;
        let { width, height } = this.designer;

        let resH = rectWidth / width;
        let resV = rectHeight / height;

        let res = Math.max(resH, resV);

        let acWidth = width * res;
        let acHeight = height * res;

        let rectCenterX = rectLeft + rectWidth / 2;
        let rectCenterY = rectTop + rectHeight / 2;

        let acLeft = rectCenterX - acWidth / 2;
        let acTop = rectCenterY - acHeight / 2;

        this.extentTarget = {
            left: acLeft,
            top: acTop,
            width: res * this.designer.width,
            height: res * this.designer.height,
        };

        this.extent.left = this.designer.extent.left;
        this.extent.top = this.designer.extent.top;
        this.extent.width = this.designer.extent.width;
        this.extent.height = this.designer.extent.height;
        this.animatorResolution.stop();
        this.animator.to(this.extentTarget, this.animationSpeed);
        this.updateAnimator();

        // this.designer.resolution = res;
        // this.designer.extent.left = acLeft;
        // this.designer.extent.top = acTop;
        // this.designer.extent.width = acWidth;
        // this.designer.extent.height = acHeight;

        // this.designer.updateLayout();
    }

    onMouseWheel(e) {

        // if (e.target && e.target.className && e.target.className.includes("diagram-ui"))
        //     return;

        let res = this.designer.resolution;

        const rect = this.designer.dom.getBoundingClientRect();
        let x = e.clientX - rect.left;
        let y = e.clientY - rect.top;

        let cx = this.designer.extent.left + x * res;
        let cy = this.designer.extent.top + y * res;

        this.backupZoomCenter = { x: cx, y: cy };
        this.backupZoomCenterScreen = { x: x, y: y };

        let d = e.deltaY;
        if (d < 0)
            res /= 2;
        else if (d > 0)
            res *= 2;

        this.resolutionTarget = {
            resolution: res,
        };

        this.resolution = this.designer.resolution;
        this.animator.stop();
        this.animatorResolution.to(this.resolutionTarget, this.animationSpeed);
        this.updateAnimator();
    }

    onMouseDown(e) {

        this.designer.dom.focus();
        this.designer.domEdit.focus();

        if (e.button === 2)
            return;
        if (this.designer.properties.isDiagramDragging)
            return;

        this.isDragging = true;
        this.designer.properties.isViewDragging = true;
        this.backupExtent = { resolution: this.designer.resolution, ...this.designer.extent };
        this.backupMouseStart = { x: e.screenX, y: e.screenY };

        e.preventDefault();
    }

    onMouseUp(e) {

        this.designer.dom.focus();
        this.designer.domEdit.focus();

        if (e.button === 2)
            return;
        if (this.designer.properties.isDiagramDragging && !this.isDragging)
            return;

        this.isDragging = false;
        this.designer.properties.isViewDragging = false;
        this.backupExtent = null;
        this.backupMouseStart = null;

        e.preventDefault();
    }

    onMouseMove(e) {

        if (this.designer.properties.isDiagramDragging) {
            this.isDragging = false;
            this.designer.properties.isViewDragging = false;
            this.backupExtent = null;
            this.backupMouseStart = null;
            return;
        }

        if (!this.isDragging)
            return;

        let dx = e.screenX - this.backupMouseStart.x;
        let dy = e.screenY - this.backupMouseStart.y;

        let offsetX = dx * this.backupExtent.resolution;
        let offsetY = dy * this.backupExtent.resolution;

        this.extentTarget = {
            left: this.backupExtent.left - offsetX,
            top: this.backupExtent.top - offsetY,
            width: this.backupExtent.width,
            height: this.backupExtent.height,
        };

        this.extent.left = this.designer.extent.left;
        this.extent.top = this.designer.extent.top;
        this.extent.width = this.designer.extent.width;
        this.extent.height = this.designer.extent.height;
        this.animatorResolution.stop();
        this.animator.to(this.extentTarget, this.animationSpeed);
        this.updateAnimator();

        e.preventDefault();
    }

    onContextmenu(e) {

        e.preventDefault();
    }
}